/**
 * Pixel: mascota en pixel art (cuadrícula de 48×48) de Reportes App.
 * En reposo es el orbe del logo de Central Media; al abrir el panel o avisar algo
 * se transforma, cuadro por cuadro, en un bot con una pantalla negra en la cara.
 * Al despertar, la pantalla muestra gráficas aleatorias (barras, línea, pastel) que
 * luego "arrancan" en una cara de terminal elegida al azar (cursor >_, ^ ^, O O, ojos arriba).
 * Gestos: wave (brazo), alert (antena naranja), think (gráficas mientras carga),
 * typing (teclea en una laptop mientras el usuario escribe). Mientras procesa, una
 * nube de pensamiento con engranes girando. Los ojos siguen al cursor (`gaze`).
 * Dormido (`sleeping`): orbe quieto con "Zzz" en pixel art.
 * Los cuadros se generan por geometría y se cachean; las animaciones van por pasos
 * y respetan prefers-reduced-motion.
 */
import React, { useEffect, useRef, useState } from 'react';

export const PIXEL_CSS = `
.cm-pixel { overflow: visible; }
.cm-pixel-sprite { animation: cm-pixel-bob 1.4s steps(1) infinite; }
.cm-pixel.is-urgent .cm-pixel-sprite, .cm-pixel.is-alert .cm-pixel-sprite { animation-duration: 0.5s; }
.cm-pixel-alt-a { animation: cm-pixel-half-a var(--cm-pixel-cycle, 1.1s) steps(1) infinite; }
.cm-pixel-alt-b { opacity: 0; animation: cm-pixel-half-b var(--cm-pixel-cycle, 1.1s) steps(1) infinite; }
.cm-pixel.is-wave { --cm-pixel-cycle: 0.6s; }
.cm-pixel.is-typing { --cm-pixel-cycle: 0.28s; }
.cm-pixel-gear { opacity: 0; animation: cm-pixel-third 0.45s steps(1) infinite; }
.cm-pixel-gear-1 { animation-delay: -0.3s; }
.cm-pixel-gear-2 { animation-delay: -0.15s; }
.cm-pixel-thought { animation: cm-pixel-bob 1.1s steps(1) infinite; }
.cm-pixel.is-asleep .cm-pixel-sprite { animation-duration: 2.8s; }
.cm-pixel-z { opacity: 0; animation: cm-pixel-z 2.4s steps(1) infinite; }
.cm-pixel-z-2 { animation-delay: 0.6s; }
.cm-pixel-z-3 { animation-delay: 1.2s; }
@keyframes cm-pixel-z {
  0% { opacity: 0; transform: translateY(0); }
  10% { opacity: 1; transform: translateY(0); }
  50% { opacity: 1; transform: translateY(-1px); }
  85% { opacity: 0; transform: translateY(-1px); }
}
@keyframes cm-pixel-third { 0% { opacity: 1; } 33.34% { opacity: 0; } 100% { opacity: 0; } }
@keyframes cm-pixel-bob { 0% { transform: translateY(0); } 50% { transform: translateY(-1px); } }
@keyframes cm-pixel-half-a { 0% { opacity: 1; } 50% { opacity: 0; } }
@keyframes cm-pixel-half-b { 0% { opacity: 0; } 50% { opacity: 1; } }
@media (prefers-reduced-motion: reduce) {
  .cm-pixel *, .cm-pixel { animation: none !important; }
  .cm-pixel-alt-b, .cm-pixel-gear { opacity: 0 !important; }
  .cm-pixel-gear-0, .cm-pixel-z { opacity: 1 !important; }
}
`;

const COLORS = {
    O: '#2A1B55', /* contorno y marco de la pantalla */
    P: '#6941D9', /* morado base del logo */
    S: '#5A3BB2', /* sombra del borde */
    L: '#CDCBFF', /* lavanda claro del anillo */
    M: '#8075ED', /* lavanda medio del anillo */
    W: '#FFFFFF', /* círculo del logo / elementos de la pantalla */
    K: '#0B0912', /* fondo de la pantalla */
    G: '#3B3552', /* elementos tenues de las gráficas */
    A: '#FF7A45', /* antena en alerta */
    N: '#4A4466', /* tapa y base de la laptop */
    V: '#9E98BA', /* borde claro de la laptop */
};

/* Geometría del logo en reposo (orbe) y ya transformado (cabeza del bot): [cx, cy, r]. */
const ORB = {
    body: [24, 24, 19.5], ring: [24.75, 22.5, 15], hole: [20.25, 24, 9.75], eye: [18.75, 24.75, 5.25],
};
const HEAD = {
    body: [24, 19, 15], ring: [24.75, 18, 12], hole: [21, 19, 8.25], eye: [20.25, 19, 5.7],
};

/* Cuadros de la transformación (0 = orbe, 1 = bot). */
export const MORPH_STEPS = [0, 0.25, 0.5, 0.75, 1];
const FRAME_MS = 85;
const BOOT_CHARTS = 3;
const BOOT_MS = 260;
const THINK_MS = 320;
const FACE_SWAP_MS = 5200;
const CHART_SEEDS = 40;

/* Caras disponibles; se elige una al azar al despertar y se rota cada tanto. */
export const FACES = ['cursor', 'caret', 'wide', 'up'];

const lerp = (a, b, t) => a + (b - a) * t;
const lerpCircle = (a, b, t) => a.map((value, i) => lerp(value, b[i], t));
const inCircle = (x, y, [cx, cy, r]) => (x + 0.5 - cx) ** 2 + (y + 0.5 - cy) ** 2 <= r * r;

const set = (px, list, color) => list.forEach(([x, y]) => px.set(`${x},${y}`, color));
const rect = (x0, y0, x1, y1) => {
    const out = [];
    for (let y = y0; y <= y1; y += 1) for (let x = x0; x <= x1; x += 1) out.push([x, y]);
    return out;
};
/* Rectángulo sin las cuatro esquinas (esquina redondeada de 1 px). */
const rounded = (x0, y0, x1, y1) => rect(x0, y0, x1, y1)
    .filter(([x, y]) => !((x === x0 || x === x1) && (y === y0 || y === y1)));

/** Pinta la cabeza/orbe con el anillo, el hueco y (opcional) el círculo blanco del logo. */
function paintOrb(px, geo, withEye) {
    const [bx, by, br] = geo.body;
    const [hx, , hr] = geo.hole;
    const [, ry, rr] = geo.ring;
    for (let y = Math.floor(by - br); y <= Math.ceil(by + br); y += 1) {
        for (let x = Math.floor(bx - br); x <= Math.ceil(bx + br); x += 1) {
            if (!inCircle(x, y, geo.body)) continue;
            let color = (x + 0.5 - bx) + (y + 0.5 - by) > br * 0.95 ? 'S' : 'P';
            if (inCircle(x, y, geo.ring)) {
                const leftSide = x + 0.5 < hx - hr * 0.45 && y + 0.5 > ry - rr * 0.6;
                color = leftSide ? 'M' : 'L';
            }
            if (inCircle(x, y, geo.hole)) color = 'P';
            if (withEye && inCircle(x, y, geo.eye)) color = 'W';
            px.set(`${x},${y}`, color);
        }
    }
}

/**
 * Caracteres de la terminal (pixel font). '#' = píxel encendido.
 * Se dibujan a partir de su esquina superior izquierda.
 */
const GLYPHS = {
    block: ['###', '###', '###', '###', '###'],
    underscore: ['...', '...', '...', '...', '###'],
    caret: ['..#..', '.#.#.', '#...#'],
    bigO: ['.###.', '#...#', '#...#', '#...#', '.###.'],
    lookUp: ['###', '###'],
    prompt: ['#.', '.#', '#.'],
    smile: ['#..#', '.##.'],
    mouthO: ['.##.', '#..#', '.##.'],
};

function glyph(name, x0, y0) {
    const out = [];
    GLYPHS[name].forEach((row, dy) => {
        [...row].forEach((ch, dx) => { if (ch === '#') out.push([x0 + dx, y0 + dy]); });
    });
    return out;
}

/* Pantalla: marco oscuro y fondo negro sobre la cara del bot. Área útil x 15..32, y 13..24. */
const SCREEN_FRAME = rounded(13, 11, 34, 26);
const SCREEN = rounded(14, 12, 33, 25);

/** Ojos y boca de terminal de cada cara: { eyes, mouth }. */
function faceParts(face, closed) {
    if (face === 'caret') {
        return { eyes: [...glyph('caret', 16, 15), ...glyph('caret', 27, 15)], mouth: glyph('smile', 22, 21) };
    }
    if (face === 'wide') {
        return { eyes: [...glyph('bigO', 16, 14), ...glyph('bigO', 27, 14)], mouth: glyph('mouthO', 22, 21) };
    }
    if (face === 'up') {
        return { eyes: [...glyph('lookUp', 18, 13), ...glyph('lookUp', 29, 13)], mouth: [[20, 23], [23, 23], [26, 23]] };
    }
    const eye = closed ? 'underscore' : 'block';
    return { eyes: [...glyph(eye, 17, 14), ...glyph(eye, 28, 14)], mouth: [...glyph('prompt', 21, 21), [24, 23], [25, 23], [26, 23]] };
}

/* Zona donde pueden moverse los ojos dentro de la pantalla (sin pisar la boca). */
const EYE_ZONE = { x0: 15, x1: 32, y0: 13, y1: 19 };

/**
 * Cara con los ojos desplazados hacia donde mira (gaze = { x: -2..2, y: -1..1 }),
 * sin salirse de la pantalla.
 */
function faceFor(face, closed, gaze = { x: 0, y: 0 }) {
    const { eyes, mouth } = faceParts(face, closed);
    const xs = eyes.map(([x]) => x);
    const ys = eyes.map(([, y]) => y);
    const clamp = (value, lo, hi) => Math.max(lo, Math.min(hi, value));
    const dx = clamp(gaze.x, EYE_ZONE.x0 - Math.min(...xs), EYE_ZONE.x1 - Math.max(...xs));
    const dy = clamp(gaze.y, EYE_ZONE.y0 - Math.min(...ys), EYE_ZONE.y1 - Math.max(...ys));
    return [...eyes.map(([x, y]) => [x + dx, y + dy]), ...mouth];
}

/** Generador pseudoaleatorio con semilla (mulberry32) para que cada gráfica se pueda cachear. */
function seeded(seed) {
    let a = Math.imul(seed + 1, 0x9E3779B1) ^ 0x85EBCA6B;
    return () => {
        a = (a + 0x6D2B79F5) | 0;
        let r = Math.imul(a ^ (a >>> 15), 1 | a);
        r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
        return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
    };
}

/**
 * Mini gráfica aleatoria para la pantalla: devuelve { W: [...], G: [...] } con los
 * píxeles blancos y los tenues.
 */
function chartFor(seed) {
    const rnd = seeded(seed);
    const kind = seed % 3;
    const white = [];
    const dim = [];
    if (kind === 0) {
        /* Barras */
        dim.push(...rect(15, 24, 32, 24));
        for (let i = 0; i < 6; i += 1) {
            const h = 2 + Math.floor(rnd() * 9);
            white.push(...rect(15 + i * 3, 24 - h, 16 + i * 3, 23));
        }
    } else if (kind === 1) {
        /* Línea (caminata aleatoria) con eje tenue */
        dim.push(...rect(15, 24, 32, 24), ...rect(15, 13, 15, 23));
        let y = 14 + Math.floor(rnd() * 8);
        let prev = y;
        for (let x = 16; x <= 32; x += 1) {
            y = Math.max(13, Math.min(23, y + Math.round((rnd() - 0.5) * 6)));
            const [lo, hi] = prev < y ? [prev, y] : [y, prev];
            white.push(...rect(x, lo, x, hi));
            prev = y;
        }
    } else {
        /* Pastel: una rebanada blanca y el resto tenue, con leyenda de barras */
        const share = 0.2 + rnd() * 0.65;
        const circle = [20.5, 18.5, 5.6];
        for (let y = 13; y <= 24; y += 1) {
            for (let x = 15; x <= 26; x += 1) {
                if (!inCircle(x, y, circle)) continue;
                const angle = Math.atan2(x + 0.5 - circle[0], -(y + 0.5 - circle[1]));
                const turn = (angle < 0 ? angle + 2 * Math.PI : angle) / (2 * Math.PI);
                (turn < share ? white : dim).push([x, y]);
            }
        }
        white.push(...rect(28, 15, 32, 15));
        dim.push(...rect(28, 18, 28 + Math.floor(rnd() * 5), 18), ...rect(28, 21, 28 + Math.floor(rnd() * 5), 21));
    }
    return { W: white, G: dim };
}

/* Piezas del cuerpo. */
const BODY = rounded(16, 33, 31, 41);
const PANEL = rect(19, 35, 28, 39);
const BARS = [...rect(20, 38, 21, 39), ...rect(23, 37, 24, 39), ...rect(26, 36, 27, 39)];
const ARM_LEFT = rounded(12, 34, 14, 39);
const ARM_RIGHT_DOWN = rounded(33, 34, 35, 39);
const ARM_RIGHT_UP = [
    ...rect(33, 33, 35, 33), ...rect(34, 32, 36, 32), ...rect(35, 30, 37, 31),
    ...rect(36, 28, 38, 29), ...rect(37, 26, 39, 27),
];
const FEET = [...rect(18, 42, 21, 42), ...rect(26, 42, 29, 42)];

/* Laptop frente a Pixel (se ve la tapa por detrás) y manos tecleando en dos cuadros. */
const LAPTOP_LID = rounded(15, 32, 32, 40);
const LAPTOP_LOGO = [[23, 35, 'L'], [24, 35, 'L'], [23, 36, 'W'], [24, 36, 'L']];
const LAPTOP_EDGE = rect(11, 41, 36, 41);
const LAPTOP_BASE = rect(12, 42, 35, 42);
const TYPING = {
    a: {
        arms: [...rounded(12, 34, 14, 38), ...rounded(33, 34, 35, 37)],
        hands: [...rect(13, 39, 15, 40), ...rect(32, 38, 34, 39)],
    },
    b: {
        arms: [...rounded(12, 34, 14, 37), ...rounded(33, 34, 35, 38)],
        hands: [...rect(13, 38, 15, 39), ...rect(32, 39, 34, 40)],
    },
};
const ANTENNA_STALK = rect(23, 1, 24, 3);
const ANTENNA_TIP = rounded(22, -1, 25, 0);

/** Agrega contorno de 1 px alrededor de todo lo pintado (vecinos en cruz). */
function outline(px) {
    const edges = [];
    px.forEach((_, key) => {
        const [x, y] = key.split(',').map(Number);
        [[1, 0], [-1, 0], [0, 1], [0, -1]].forEach(([dx, dy]) => {
            const k = `${x + dx},${y + dy}`;
            if (!px.has(k)) edges.push(k);
        });
    });
    edges.forEach((k) => px.set(k, 'O'));
}

/** Une píxeles contiguos del mismo color por fila para dibujar menos <rect>. */
function toRects(px) {
    const rows = new Map();
    px.forEach((color, key) => {
        const [x, y] = key.split(',').map(Number);
        if (!rows.has(y)) rows.set(y, []);
        rows.get(y).push([x, color]);
    });
    const rects = [];
    rows.forEach((cells, y) => {
        cells.sort((a, b) => a[0] - b[0]);
        let run = null;
        cells.forEach(([x, color]) => {
            if (run && x === run.x + run.w && color === run.color) {
                run.w += 1;
                return;
            }
            if (run) rects.push(run);
            run = { x, y, color, w: 1 };
        });
        if (run) rects.push(run);
    });
    return rects;
}

const cache = new Map();

/**
 * Cuadro del sprite.
 * t: avance de la transformación (0 orbe → 1 bot). En t = 1 la pantalla muestra
 * la gráfica `chart` (semilla) si viene, o la cara `face`.
 */
export function buildFrame(t, {
    face = 'cursor', closed = false, armUp = false, chart = null, alert = false,
    gaze = { x: 0, y: 0 }, typing = null,
} = {}) {
    const key = `${t}|${face}|${closed}|${armUp}|${chart}|${alert}|${gaze.x},${gaze.y}|${typing}`;
    if (cache.has(key)) return cache.get(key);

    const px = new Map();
    const geo = {
        body: lerpCircle(ORB.body, HEAD.body, t),
        ring: lerpCircle(ORB.ring, HEAD.ring, t),
        hole: lerpCircle(ORB.hole, HEAD.hole, t),
        eye: lerpCircle(ORB.eye, HEAD.eye, t),
    };
    /* En forma de orbe, el círculo blanco del logo también sigue al cursor. */
    geo.eye = [geo.eye[0] + gaze.x * 0.8, geo.eye[1] + gaze.y * 0.8, geo.eye[2]];

    if (t >= 0.5) {
        const lastRow = t >= 0.75 ? 41 : 36;
        set(px, BODY.filter(([, y]) => y <= lastRow), 'P');
        set(px, PANEL.filter(([, y]) => y <= lastRow), 'L');
        set(px, BARS.filter(([, y]) => y <= lastRow), 'P');
    }
    if (t >= 0.75 && !(t >= 1 && typing)) {
        set(px, ARM_LEFT, 'P');
        set(px, armUp ? ARM_RIGHT_UP : ARM_RIGHT_DOWN, 'P');
    }
    if (t >= 1 && typing) set(px, TYPING[typing].arms, 'P');
    if (t >= 1) {
        set(px, FEET, 'P');
        set(px, ANTENNA_STALK, 'O');
        set(px, ANTENNA_TIP, alert ? 'A' : 'L');
    }

    paintOrb(px, geo, t < 1);

    if (t >= 1) {
        set(px, SCREEN_FRAME, 'O');
        set(px, SCREEN, 'K');
        if (chart != null) {
            const { W, G } = chartFor(chart);
            set(px, G, 'G');
            set(px, W, 'W');
        } else {
            set(px, faceFor(face, closed, gaze), 'W');
        }
        if (typing) {
            set(px, LAPTOP_LID, 'N');
            LAPTOP_LOGO.forEach(([x, y, color]) => px.set(`${x},${y}`, color));
            set(px, LAPTOP_EDGE, 'V');
            set(px, LAPTOP_BASE, 'N');
            set(px, TYPING[typing].hands, 'P');
        }
    }

    outline(px);

    const rects = toRects(px);
    cache.set(key, rects);
    return rects;
}

/* Nube de pensamiento (a la derecha y arriba de la cabeza) con dos engranes que engranan. */
const CLOUD = [[47, -8, 5.5], [53.5, -11, 6], [60, -8, 5.5], [53.5, -4.5, 6.5], [46, -3.5, 4], [61, -3.5, 4]];
const CLOUD_BUBBLES = [[37.5, 8.5, 1.1], [40.5, 4.5, 1.6]];
const GEARS = [
    { cx: 50.5, cy: -7, body: 3, tip: 4.9, teeth: 6, color: 'P', dir: 1 },
    { cx: 58.5, cy: -5, body: 1.9, tip: 3.3, teeth: 5, color: 'M', dir: -1 },
];
export const GEAR_FRAMES = 3;

let thoughtCache = null;

/** Rectángulos de la nube (base) y de cada cuadro de los engranes girando. */
export function buildThought() {
    if (thoughtCache) return thoughtCache;
    const cloud = new Map();
    [...CLOUD, ...CLOUD_BUBBLES].forEach((circle) => {
        const [cx, cy, r] = circle;
        for (let y = Math.floor(cy - r); y <= Math.ceil(cy + r); y += 1) {
            for (let x = Math.floor(cx - r); x <= Math.ceil(cx + r); x += 1) {
                if (inCircle(x, y, circle)) cloud.set(`${x},${y}`, 'W');
            }
        }
    });
    outline(cloud);

    const frames = Array.from({ length: GEAR_FRAMES }, (_, frame) => {
        const px = new Map();
        GEARS.forEach(({ cx, cy, body, tip, teeth, color, dir }, index) => {
            const phase = dir * frame * (2 * Math.PI / teeth / GEAR_FRAMES) + (index ? Math.PI / teeth : 0);
            for (let y = Math.floor(cy - tip); y <= Math.ceil(cy + tip); y += 1) {
                for (let x = Math.floor(cx - tip); x <= Math.ceil(cx + tip); x += 1) {
                    const dxp = x + 0.5 - cx;
                    const dyp = y + 0.5 - cy;
                    const dist = Math.hypot(dxp, dyp);
                    const tooth = Math.cos(teeth * (Math.atan2(dyp, dxp) - phase)) > 0.35;
                    if (dist <= 1.1) px.set(`${x},${y}`, 'W');
                    else if (dist <= body || (dist <= tip && tooth)) px.set(`${x},${y}`, color);
                }
            }
        });
        return toRects(px);
    });
    thoughtCache = { cloud: toRects(cloud), frames };
    return thoughtCache;
}

/* "Zzz" al dormir: tres Z de tamaño creciente arriba del orbe, hacia el lado libre. */
const Z_GLYPHS = [
    { rows: ['###', '.#.', '###'], x: 38, y: 4 },
    { rows: ['####', '..#.', '.#..', '####'], x: 42, y: -2 },
    { rows: ['#####', '...#.', '..#..', '.#...', '#####'], x: 47, y: -9 },
];
const zzzCache = new Map();

/** Rectángulos de cada Z (con contorno); del lado izquierdo se reflejan las posiciones, no las letras. */
export function buildZzz(side = 'right', low = false) {
    const key = `${side}|${low}`;
    if (zzzCache.has(key)) return zzzCache.get(key);
    const letters = Z_GLYPHS.map(({ rows, x, y }) => {
        const width = rows[0].length;
        const left = side === 'left' ? 48 - x - width : x;
        const top = y + (low ? 6 : 0);
        const px = new Map();
        rows.forEach((row, dy) => {
            [...row].forEach((ch, dx) => { if (ch === '#') px.set(`${left + dx},${top + dy}`, 'L'); });
        });
        outline(px);
        return toRects(px);
    });
    zzzCache.set(key, letters);
    return letters;
}

function prefersReducedMotion() {
    try {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (error) {
        return false;
    }
}

const randomSeed = () => Math.floor(Math.random() * CHART_SEEDS);
const randomFace = (except) => {
    const pool = FACES.filter((face) => face !== except);
    return pool[Math.floor(Math.random() * pool.length)];
};

/** Avanza cuadro por cuadro hacia el orbe (0) o hacia el bot (último paso). */
function useMorphStep(isBot) {
    const goal = isBot ? MORPH_STEPS.length - 1 : 0;
    const [step, setStep] = useState(goal);
    useEffect(() => {
        if (step === goal) return undefined;
        if (prefersReducedMotion()) {
            setStep(goal);
            return undefined;
        }
        const timer = window.setTimeout(() => setStep((s) => s + (goal > s ? 1 : -1)), FRAME_MS);
        return () => window.clearTimeout(timer);
    }, [step, goal]);
    return step;
}

/**
 * Pantalla del bot: al despertar muestra BOOT_CHARTS gráficas y luego una cara al azar;
 * rota de cara cada FACE_SWAP_MS con un destello de gráfica; mientras piensa, solo gráficas.
 */
function useScreen(isBot, thinking) {
    const [face, setFace] = useState(() => randomFace());
    const [chart, setChart] = useState(null);
    const [bootLeft, setBootLeft] = useState(0);
    const wasBot = useRef(false);

    useEffect(() => {
        if (isBot && !wasBot.current) {
            setFace((prev) => randomFace(prev));
            if (prefersReducedMotion()) {
                setBootLeft(0);
                setChart(null);
            } else {
                setBootLeft(BOOT_CHARTS);
                setChart(randomSeed());
            }
        }
        wasBot.current = isBot;
    }, [isBot]);

    useEffect(() => {
        if (!isBot || bootLeft <= 0) return undefined;
        const timer = window.setTimeout(() => {
            const left = bootLeft - 1;
            setBootLeft(left);
            setChart(left > 0 ? randomSeed() : null);
        }, BOOT_MS);
        return () => window.clearTimeout(timer);
    }, [isBot, bootLeft]);

    useEffect(() => {
        if (!isBot || !thinking || prefersReducedMotion()) return undefined;
        setChart(randomSeed());
        const timer = window.setInterval(() => setChart(randomSeed()), THINK_MS);
        return () => {
            window.clearInterval(timer);
            setChart(null);
        };
    }, [isBot, thinking]);

    useEffect(() => {
        if (!isBot || thinking) return undefined;
        const timer = window.setInterval(() => {
            setFace((prev) => randomFace(prev));
            if (!prefersReducedMotion()) {
                setBootLeft(1);
                setChart(randomSeed());
            }
        }, FACE_SWAP_MS);
        return () => window.clearInterval(timer);
    }, [isBot, thinking]);

    return { face, chart };
}

function Frame({ rects, className }) {
    return (
        <g className={className}>
            {rects.map((r) => (
                <rect key={`${r.x},${r.y}`} x={r.x} y={r.y} width={r.w} height={1} fill={COLORS[r.color]} />
            ))}
        </g>
    );
}

/** "Zzz" animado mientras Pixel duerme. */
function Zzz({ side, low }) {
    return (
        <g>
            {buildZzz(side, low).map((rects, index) => (
                <Frame key={index} className={`cm-pixel-z cm-pixel-z-${index + 1}`} rects={rects} />
            ))}
        </g>
    );
}

/** Nube de pensamiento con engranes; `side` la refleja hacia la izquierda y `low` la baja. */
function Thought({ side, low }) {
    const { cloud, frames } = buildThought();
    const transforms = [low ? 'translate(0 14)' : '', side === 'left' ? 'translate(48 0) scale(-1 1)' : ''].filter(Boolean).join(' ');
    return (
        <g className="cm-pixel-thought" transform={transforms || undefined}>
            <Frame rects={cloud} />
            {frames.map((rects, index) => (
                <Frame key={index} className={`cm-pixel-gear cm-pixel-gear-${index}`} rects={rects} />
            ))}
        </g>
    );
}

/**
 * Mascota Pixel. Con size = 100 cada píxel del sprite mide 2 px en pantalla.
 * @param {{
 *   form?: 'orb'|'bot', mood?: 'idle'|'wave'|'alert'|'think'|'typing', urgent?: boolean,
 *   gaze?: { x: number, y: number }, thinking?: boolean, thoughtSide?: 'left'|'right',
 *   thoughtLow?: boolean, sleeping?: boolean, size?: number, title?: string
 * }} props
 */
export default function PixelMascot({
    form = 'orb', mood = 'idle', urgent = false, gaze = { x: 0, y: 0 }, thinking = false,
    thoughtSide = 'right', thoughtLow = false, sleeping = false, size = 100, title = 'Pixel',
}) {
    const step = useMorphStep(form === 'bot');
    const t = MORPH_STEPS[step];
    const isBot = t === 1;
    const { face, chart } = useScreen(isBot, mood === 'think' || thinking);
    const alert = mood === 'alert';
    const asleep = sleeping && step === 0;
    /* Dormido no sigue al cursor. */
    const look = asleep ? { x: 0, y: 0 } : { x: gaze?.x || 0, y: gaze?.y || 0 };

    let frames;
    if (!isBot) {
        frames = [buildFrame(t, { gaze: look })];
    } else if (mood === 'typing') {
        /* Mira hacia abajo, al teclado, mientras teclea. */
        const down = { x: look.x, y: 1 };
        frames = [buildFrame(1, { face, chart, gaze: down, typing: 'a' }), buildFrame(1, { face, chart, gaze: down, typing: 'b' })];
    } else if (mood === 'wave') {
        frames = [buildFrame(1, { face, chart, gaze: look, armUp: true }), buildFrame(1, { face, chart, gaze: look, armUp: false })];
    } else if (chart == null && face === 'cursor') {
        frames = [buildFrame(1, { face, alert, gaze: look }), buildFrame(1, { face, alert, gaze: look, closed: true })];
    } else {
        frames = [buildFrame(1, { face, chart, alert, gaze: look })];
    }

    const classes = ['cm-pixel', isBot ? `is-${mood}` : 'is-orb', asleep ? 'is-asleep' : '', urgent && !asleep ? 'is-urgent' : '']
        .filter(Boolean).join(' ');
    return (
        <svg
            className={classes}
            width={size}
            height={size}
            viewBox="-2 -2 50 50"
            shapeRendering="crispEdges"
            role="img"
            aria-label={title}
        >
            <rect x="15" y="45" width="18" height="1" fill="rgba(36, 22, 64, 0.16)" />
            <g className="cm-pixel-sprite">
                {frames.length === 2 ? (
                    <>
                        <Frame className="cm-pixel-alt-a" rects={frames[0]} />
                        <Frame className="cm-pixel-alt-b" rects={frames[1]} />
                    </>
                ) : (
                    <Frame rects={frames[0]} />
                )}
            </g>
            {isBot && thinking && <Thought side={thoughtSide} low={thoughtLow} />}
            {asleep && <Zzz side={thoughtSide} low={thoughtLow} />}
        </svg>
    );
}
